// src/pages/ManageCustomers.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";

const API_BASE = process.env.REACT_APP_API_BASE; // https://api.bookyourneed.com/api

const ManageCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null); // full details
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // ============================================================
  // 📡 Fetch all customers (list view)
  // ============================================================
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/admin/customers`);
        // backend returns an array of users
        setCustomers(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("❌ Failed to fetch customers:", err);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  // ============================================================
  // 📡 Fetch full customer details for modal
  // ============================================================
  const openCustomer = async (customerId) => {
    try {
      setDetailsLoading(true);
      setModalOpen(true);
      setSelectedCustomer(null); // reset until loaded

      const res = await axios.get(
        `${API_BASE}/admin/customers/${customerId}/details`
      );

      setSelectedCustomer(res.data);
    } catch (err) {
      console.error("❌ Failed to fetch customer details:", err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedCustomer(null);
  };

  return (
    <div className="flex">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-6 w-full bg-gray-100 min-h-screen font-[Poppins]">
        <h1 className="text-3xl font-extrabold mb-8 text-gray-800">
          📋 Manage Customers
        </h1>

        {/* Table Section */}
        {loading ? (
          <p className="text-gray-600">Loading customers...</p>
        ) : customers.length === 0 ? (
          <p className="text-gray-500">No customers found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-2xl shadow text-sm">
              <thead className="bg-gray-200 text-gray-700">
                <tr>
                  <th className="py-3 px-4 text-left">Name</th>
                  <th className="py-3 px-4 text-left">Email</th>
                  <th className="py-3 px-4 text-left">Phone</th>
                  <th className="py-3 px-4 text-left">City</th>
                  <th className="py-3 px-4 text-left"># Jobs</th>
                  <th className="py-3 px-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c) => (
                  <tr
                    key={c._id}
                    className="border-t hover:bg-gray-50 transition duration-200"
                  >
                    <td className="py-2 px-4">
                      {c.name || c.email.split("@")[0]}
                    </td>
                    <td className="py-2 px-4">{c.email}</td>
                    <td className="py-2 px-4">{c.phone || "N/A"}</td>
                    <td className="py-2 px-4">{c.city || "N/A"}</td>
                    <td className="py-2 px-4 font-semibold text-indigo-700">
                      {c.jobCount || 0}
                    </td>
                    <td className="py-2 px-4">
                      <button
                        className="text-blue-600 hover:text-blue-800 underline font-medium"
                        onClick={() => openCustomer(c._id)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ================= Modal: Customer Detail ================= */}
        {modalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-2xl shadow-2xl max-w-4xl w-full relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={closeModal}
                className="absolute top-3 right-4 text-xl text-gray-500 hover:text-red-500 transition"
              >
                ✕
              </button>

              {detailsLoading || !selectedCustomer ? (
                <p className="text-gray-600 text-center py-8">
                  Loading customer details…
                </p>
              ) : (
                <>
                  {/* Header */}
                  <h2 className="text-2xl font-bold mb-3 text-gray-800">
                    {selectedCustomer.customer.name ||
                      selectedCustomer.customer.email}
                  </h2>

                  {/* Basic Info */}
                  <div className="text-gray-700 space-y-1 text-sm mb-4">
                    <p>
                      <strong>Email:</strong>{" "}
                      {selectedCustomer.customer.email}
                    </p>
                    <p>
                      <strong>Phone:</strong>{" "}
                      {selectedCustomer.customer.phone || "N/A"}
                    </p>
                    <p>
                      <strong>Street:</strong>{" "}
                      {selectedCustomer.customer.street || "N/A"}
                    </p>
                    <p>
                      <strong>City:</strong>{" "}
                      {selectedCustomer.customer.city || "N/A"}
                    </p>
                    <p>
                      <strong>Province:</strong>{" "}
                      {selectedCustomer.customer.province || "N/A"}
                    </p>
                    <p>
                      <strong>Postal Code:</strong>{" "}
                      {selectedCustomer.customer.postalCode || "N/A"}
                    </p>
                    <p className="text-gray-500 text-xs mt-2">
                      Account created on:{" "}
                      {new Date(
                        selectedCustomer.customer.createdAt
                      ).toLocaleDateString()}
                    </p>
                  </div>

                  {/* Stats */}
                  <h3 className="text-lg font-semibold mt-2 mb-2 text-gray-800">
                    📊 Customer Stats
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-xs md:text-sm">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="text-gray-500">Total Jobs</div>
                      <div className="text-lg font-bold">
                        {selectedCustomer.stats.totalJobs}
                      </div>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="text-gray-500">Completed</div>
                      <div className="text-lg font-bold text-green-700">
                        {selectedCustomer.stats.completed}
                      </div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg p-3">
                      <div className="text-gray-500">Active</div>
                      <div className="text-lg font-bold text-yellow-700">
                        {selectedCustomer.stats.active}
                      </div>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3">
                      <div className="text-gray-500">Cancelled</div>
                      <div className="text-lg font-bold text-red-700">
                        {selectedCustomer.stats.cancelled}
                      </div>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3">
                      <div className="text-gray-500">Disputed</div>
                      <div className="text-lg font-bold text-orange-700">
                        {selectedCustomer.stats.disputed}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3 col-span-2 md:col-span-2">
                      <div className="text-gray-500">Total Spent</div>
                      <div className="text-lg font-bold text-blue-700">
                        ${selectedCustomer.stats.totalValue.toFixed(2)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Released: $
                        {selectedCustomer.stats.totalReleased.toFixed(2)} |{" "}
                        Refunded: $
                        {selectedCustomer.stats.totalRefunded.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  {/* Jobs */}
                  <h3 className="text-lg font-semibold mt-4 mb-2 text-gray-800">
                    📝 Job History
                  </h3>

                  {selectedCustomer.jobs.length === 0 ? (
                    <p className="text-gray-500 text-sm">
                      No jobs found for this customer.
                    </p>
                  ) : (
                    <div className="overflow-x-auto border rounded-xl">
                      <table className="min-w-full text-xs md:text-sm">
                        <thead className="bg-gray-100 text-gray-700">
                          <tr>
                            <th className="py-2 px-3 text-left">Job</th>
                            <th className="py-2 px-3 text-left">Status</th>
                            <th className="py-2 px-3 text-left">
                              Payment
                            </th>
                            <th className="py-2 px-3 text-left">Amount</th>
                            <th className="py-2 px-3 text-left">Worker</th>
                            <th className="py-2 px-3 text-left">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedCustomer.jobs.map((job) => (
                            <tr
                              key={job.id}
                              className="border-t hover:bg-gray-50"
                            >
                              <td className="py-2 px-3">
                                <div className="font-semibold">
                                  {job.jobTitle || "Untitled Job"}
                                </div>
                                {job.city && (
                                  <div className="text-[11px] text-gray-500">
                                    {job.city}, {job.province}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3">
                                <span className="inline-flex px-2 py-1 rounded-full bg-gray-100 text-[11px] capitalize">
                                  {job.status}
                                </span>
                              </td>
                              <td className="py-2 px-3 text-[11px] capitalize">
                                {job.paymentStatus || "unpaid"}
                              </td>
                              <td className="py-2 px-3 text-sm">
                                {job.assignedPrice
                                  ? `$${job.assignedPrice.toFixed(2)}`
                                  : "-"}
                                {job.refundAmount > 0 && (
                                  <div className="text-[11px] text-red-600">
                                    Refunded: ${job.refundAmount.toFixed(2)}
                                  </div>
                                )}
                              </td>
                              <td className="py-2 px-3 text-[11px]">
                                {job.assignedWorker
                                  ? job.assignedWorker.name ||
                                    job.assignedWorker.email
                                  : "Not assigned"}
                              </td>
                              <td className="py-2 px-3 text-[11px]">
                                {new Date(job.createdAt).toLocaleDateString()}
                                {job.scheduledAt && (
                                  <div className="text-[10px] text-gray-500">
                                    Scheduled:{" "}
                                    {new Date(
                                      job.scheduledAt
                                    ).toLocaleDateString()}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageCustomers;
